/**
 * Data Source Types Hook
 *
 * Custom hook for fetching available data source types
 * and their configuration requirements.
 */

import { useEffect, useState } from 'react';

interface DataSourceType {
  type: string;
  name: string;
  description: string;
  capabilities: string[];
  requiredFields: string[];
  optionalFields: string[];
}

export const useDataSourceTypes = () => {
  const [dataSourceTypes, setDataSourceTypes] = useState<DataSourceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDataSourceTypes = async () => {
      try {
        // Mock API call - in real app, would call actual API
        await new Promise(resolve => setTimeout(resolve, 500));

        const types: DataSourceType[] = [
          {
            type: 'azure-devops',
            name: 'Azure DevOps',
            description:
              'Integrate with Azure DevOps for work items, builds, and releases',
            capabilities: ['work-items', 'builds', 'releases', 'repositories'],
            requiredFields: ['organizationUrl', 'personalAccessToken'],
            optionalFields: ['defaultProject', 'workItemTypes'],
          },
          {
            type: 'github',
            name: 'GitHub',
            description:
              'Integrate with GitHub for repositories, pull requests, and deployments',
            capabilities: [
              'repositories',
              'pull-requests',
              'commits',
              'deployments',
              'workflows',
            ],
            requiredFields: ['personalAccessToken'],
            optionalFields: [
              'apiUrl',
              'defaultOrganization',
              'defaultRepositories',
            ],
          },
          {
            type: 'cicd',
            name: 'CI/CD Pipeline',
            description:
              'Integrate with CI/CD systems for build and deployment data',
            capabilities: ['pipelines', 'builds', 'deployments'],
            requiredFields: ['apiUrl', 'apiToken'],
            optionalFields: ['apiVersion', 'defaultPipelines', 'customHeaders'],
          },
        ];

        setDataSourceTypes(types);
      } catch (err: any) {
        setError(err.message || 'Failed to load data source types');
      } finally {
        setLoading(false);
      }
    };

    fetchDataSourceTypes();
  }, []);

  return {
    dataSourceTypes,
    loading,
    error,
  };
};
